/**
 * Report generator page - Main interface for generating reports.
 */
import { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'

import { apiClient, ReportGenerateRequest } from '@/services/api'
import { NotesInput } from '@/components/Generator/NotesInput'
import { ModelSelector } from '@/components/Generator/ModelSelector'
import { ReasoningEffortSelector } from '@/components/Generator/ReasoningEffortSelector'
import { ReportViewer } from '@/components/Generator/ReportViewer'
import { DownloadButton } from '@/components/Generator/DownloadButton'

export const GeneratorPage: React.FC = () => {
  const { getToken } = useAuth()

  // Form state
  const [notes, setNotes] = useState('')
  const [model, setModel] = useState<'gpt-5' | 'gpt-5-mini' | 'gpt-5-nano'>('gpt-5-mini')
  const [reasoningEffort, setReasoningEffort] = useState<
    'minimal' | 'low' | 'medium' | 'high'
  >('medium')
  const [useSamples, setUseSamples] = useState(true)

  // Generated report state
  const [generatedReport, setGeneratedReport] = useState<{
    text: string
    downloadUrl: string | null
    tokensUsed: number | null
    modelUsed: string
    reasoningEffort: string | null
    samplesUsed: number
  } | null>(null)

  // Set token getter on mount
  useEffect(() => {
    if (getToken) {
      apiClient.setTokenGetter(getToken)
    }
  }, [getToken])

  // Fetch samples count
  const { data: samplesData } = useQuery({
    queryKey: ['samples'],
    queryFn: () => apiClient.listSamples(),
  })

  const samplesCount = samplesData?.count || 0

  // Generate report mutation
  const generateMutation = useMutation({
    mutationFn: (request: ReportGenerateRequest) => apiClient.generateReport(request),
    onSuccess: (response) => {
      setGeneratedReport({
        text: response.report_text,
        downloadUrl: response.download_url,
        tokensUsed: response.tokens_used,
        modelUsed: response.model_used,
        reasoningEffort: response.reasoning_effort,
        samplesUsed: response.samples_used,
      })
      // Scroll to report
      setTimeout(() => {
        document.getElementById('generated-report')?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    },
    onError: (error: Error) => {
      alert(`Failed to generate report: ${error.message}`)
    },
  })

  const handleGenerate = () => {
    if (!notes.trim()) {
      alert('Please enter meeting notes')
      return
    }

    if (notes.length > 10000) {
      alert('Notes are too long. Maximum 10,000 characters.')
      return
    }

    const request: ReportGenerateRequest = {
      notes,
      model,
      reasoning_effort: model === 'gpt-5' ? reasoningEffort : undefined,
      use_samples: useSamples,
    }

    generateMutation.mutate(request)
  }

  const handleReset = () => {
    setGeneratedReport(null)
    setNotes('')
  }

  // Show reasoning effort only for GPT-5
  const showReasoningEffort = model === 'gpt-5'

  // Estimate generation time
  const getEstimatedTime = () => {
    if (model === 'gpt-5-nano') return '5-15 seconds'
    if (model === 'gpt-5-mini') return '10-20 seconds'
    // GPT-5
    if (reasoningEffort === 'minimal') return '10-20 seconds'
    if (reasoningEffort === 'low') return '15-25 seconds'
    if (reasoningEffort === 'medium') return '20-40 seconds'
    if (reasoningEffort === 'high') return '30-60 seconds'
    return '20-40 seconds'
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Generate Report</h1>
        <p className="text-gray-600">
          Transform your meeting notes into a comprehensive, professional report using AI.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Input Form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            {/* Notes Input */}
            <NotesInput
              value={notes}
              onChange={setNotes}
              disabled={generateMutation.isPending}
            />

            {/* Model Selection */}
            <div className="mt-6">
              <ModelSelector
                value={model}
                onChange={(newModel) => {
                  setModel(newModel)
                  // Reset reasoning effort when switching to/from GPT-5
                  if (newModel !== 'gpt-5') {
                    setReasoningEffort('medium')
                  }
                }}
                disabled={generateMutation.isPending}
              />
            </div>

            {/* Reasoning Effort (only for GPT-5) */}
            {showReasoningEffort && (
              <div className="mt-6">
                <ReasoningEffortSelector
                  value={reasoningEffort}
                  onChange={setReasoningEffort}
                  disabled={generateMutation.isPending}
                />
              </div>
            )}

            {/* Training Samples Toggle */}
            <div className="mt-6">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useSamples}
                  onChange={(e) => setUseSamples(e.target.checked)}
                  disabled={generateMutation.isPending || samplesCount === 0}
                  className="w-5 h-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">
                  Use my training samples
                  {samplesCount > 0 && (
                    <span className="ml-2 text-primary-600">
                      ({samplesCount} sample{samplesCount !== 1 ? 's' : ''} available)
                    </span>
                  )}
                  {samplesCount === 0 && (
                    <span className="ml-2 text-gray-500">(No samples available)</span>
                  )}
                </span>
              </label>
              <p className="text-sm text-gray-600 mt-1 ml-8">
                {samplesCount > 0
                  ? 'The AI will match your preferred report style and structure'
                  : 'Add training samples on the Training Samples page to improve quality'}
              </p>
            </div>
          </div>
        </div>

        {/* Right Column: Actions & Info */}
        <div className="lg:col-span-1 space-y-6">
          {/* Generate Button */}
          <div className="card">
            <button
              onClick={handleGenerate}
              disabled={!notes.trim() || generateMutation.isPending}
              className="btn-primary w-full py-4 text-lg"
            >
              {generateMutation.isPending ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating Report...
                </>
              ) : (
                <>
                  <svg
                    className="w-6 h-6 inline-block mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  Generate Report
                </>
              )}
            </button>

            {generateMutation.isPending && (
              <div className="mt-4 text-center text-sm text-gray-600">
                <p>Estimated time: {getEstimatedTime()}</p>
                <p className="mt-1 text-xs text-gray-500">
                  Using {model} {showReasoningEffort && `with ${reasoningEffort} reasoning`}
                </p>
              </div>
            )}

            {generatedReport && (
              <button
                onClick={handleReset}
                className="btn-secondary w-full mt-3"
              >
                Generate Another Report
              </button>
            )}
          </div>

          {/* Info Card */}
          <div className="card bg-blue-50 border border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-3">💡 Tips for Better Reports</h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• Include key discussion points and decisions</li>
              <li>• Note action items with owners</li>
              <li>• Mention specific dates and deadlines</li>
              <li>• Use bullet points or short paragraphs</li>
              <li>• Upload training samples for personalized output</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Generated Report Section */}
      {generatedReport && (
        <div id="generated-report" className="mt-12 space-y-6">
          <ReportViewer
            report={generatedReport.text}
            tokensUsed={generatedReport.tokensUsed}
            modelUsed={generatedReport.modelUsed}
            reasoningEffort={generatedReport.reasoningEffort}
            samplesUsed={generatedReport.samplesUsed}
          />

          {/* Download Button */}
          <div className="card text-center">
            <DownloadButton
              downloadUrl={generatedReport.downloadUrl}
              onDownload={() => {
                console.log('Download initiated')
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
